'use client';

import { useState } from 'react';

import { usePatchJobDeliverableConfidenceMutation } from '@/features/jobs/api/jobsApi';
import { SmartInput } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import type { ProgressConfidence } from '@/features/jobs/types';

type DeliverableConfidenceFieldProps = {
  deliverableId: string;
  value: ProgressConfidence | undefined;
  readOnly: boolean;
  onPatched?: (next: ProgressConfidence) => void;
};

const OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export function DeliverableConfidenceField({
  deliverableId,
  value,
  readOnly,
  onPatched,
}: DeliverableConfidenceFieldProps) {
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [patchConfidence, { isLoading }] =
    usePatchJobDeliverableConfidenceMutation();
  const resolvedValue = pendingValue ?? value ?? '';

  return (
    <SmartInput
      type="select"
      name={`deliverable-confidence-${deliverableId}`}
      label="Confidence"
      value={resolvedValue}
      options={OPTIONS}
      readOnly={readOnly || isLoading}
      onChange={(nextValue) => {
        const next = String(nextValue ?? '');
        if (readOnly) {
          return;
        }

        const payloadValue = (next || null) as ProgressConfidence;
        setPendingValue(next);
        void patchConfidence({
          deliverableId,
          payload: {
            progress_confidence: payloadValue,
          },
        })
          .unwrap()
          .then(() => {
            setPendingValue(null);
            onPatched?.(payloadValue);
          })
          .catch((error) => {
            setPendingValue(null);
            notify({ type: 'error', message: getErrorMessage(error) });
          });
      }}
    />
  );
}
